import { FolderContent } from '@/api/generatedApi';
import { Box, ListItemIcon, ListItemText } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { RemoveAssetFromGroupModal } from '../modals/RemoveAssetFromGroupModal';
import { MoveToGroupModal } from '../modals/MoveToGroupModal';
import { StyledList, StyledListItem } from '@/components/AssetsPage/PopoverList';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { CopyMenuItem } from '@/components/ContextMenu/CopyMenuItem';
import { CopiableColumn } from '@/types/copiableColumn';
import { GridColDef } from '@mui/x-data-grid-pro';

export const ReportContextMenu = <T extends FolderContent>({
  anchorPosition,
  onClose,
  clickedItem,
  clickedField,
  columns,
  reportId,
  groupId,
  assetCount,
  onRemoved,
  canManageGroups = true,
}: {
  anchorPosition: { left: number; top: number } | undefined;
  onClose: () => void;
  clickedItem: T | undefined;
  clickedField?: string;
  columns?: (GridColDef<T> & Partial<CopiableColumn<T>>)[];
  reportId: string;
  groupId: string;
  assetCount: number;
  onRemoved?: () => void;
  canManageGroups?: boolean;
}) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<FolderContent | undefined>();
  const [itemToMove, setItemToMove] = useState<FolderContent | undefined>();
  const contextMenuReference = useRef<HTMLDivElement>(null);

  const showAssetActions = canManageGroups && !!clickedItem && clickedItem.contentType === 'asset';

  const copyValue =
    clickedItem && clickedField && columns
      ? columns.find(col => col.field === clickedField)?.getCopyValue?.(clickedItem)
      : undefined;

  const showContextMenu = showAssetActions || !!copyValue;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuReference.current && !contextMenuReference.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (anchorPosition) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorPosition, onClose]);

  const handleRemoveClick = () => {
    if (clickedItem?.contentType === 'asset') {
      setItemToRemove(clickedItem);
      setIsRemoveModalOpen(true);
    }
    onClose();
  };

  const handleMoveClick = () => {
    if (clickedItem?.contentType === 'asset') {
      setItemToMove(clickedItem);
      setIsMoveModalOpen(true);
    }
    onClose();
  };

  const handleRemoveModalClose = () => {
    setIsRemoveModalOpen(false);
    setItemToRemove(undefined);
  };

  const handleMoveModalClose = () => {
    setIsMoveModalOpen(false);
    setItemToMove(undefined);
  };

  const handleRemove = () => {
    setIsRemoveModalOpen(false);
    setItemToRemove(undefined);
    onRemoved?.();
  };

  const handleMoveSuccess = () => {
    onRemoved?.();
  };

  return (
    <>
      {anchorPosition && showContextMenu && (
        <Box
          sx={{
            position: 'fixed',
            left: anchorPosition.left,
            top: anchorPosition.top,
            zIndex: 1300,
            backgroundColor: 'background.paper',
            borderRadius: '12px',
            minWidth: '160px',
            boxShadow: 8,
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            overflow: 'hidden',
          }}
          ref={contextMenuReference}
        >
          <StyledList sx={{ padding: 0 }}>
            {!!copyValue && columns && (
              <CopyMenuItem columns={columns} row={clickedItem} field={clickedField} onCopy={onClose} />
            )}
            {showAssetActions && (
              <>
                <StyledListItem onClick={handleMoveClick}>
                  <ListItemIcon>
                    <DriveFileMoveIcon sx={{ fontSize: '18px' }} />
                  </ListItemIcon>
                  <ListItemText>Move to Group</ListItemText>
                </StyledListItem>
                <StyledListItem onClick={handleRemoveClick}>
                  <ListItemIcon>
                    <RemoveCircleOutlineIcon sx={{ fontSize: '18px' }} />
                  </ListItemIcon>
                  <ListItemText>Remove from Group</ListItemText>
                </StyledListItem>
              </>
            )}
          </StyledList>
        </Box>
      )}
      {itemToRemove?.contentType === 'asset' && (
        <RemoveAssetFromGroupModal
          isOpen={isRemoveModalOpen}
          onClose={handleRemoveModalClose}
          onRemove={handleRemove}
          asset={itemToRemove}
          reportId={reportId}
          groupId={groupId}
          assetCount={assetCount}
        />
      )}
      {itemToMove?.contentType === 'asset' && (
        <MoveToGroupModal
          open={isMoveModalOpen}
          onClose={handleMoveModalClose}
          onSuccess={handleMoveSuccess}
          currentGroupId={groupId}
          assetIds={[itemToMove.id]}
          reportId={reportId}
        />
      )}
    </>
  );
};
